"use client";

import { sendGTMEvent } from "@next/third-parties/google";
import {
  DEFAULT_COUNTRY,
  DEFAULT_CURRENCY,
  DEFAULT_LOCALE,
  DEFAULT_MEASUREMENT,
  regexForLang,
  regexForLangAndCountryCode,
  storageUserCountry,
} from "@utils/default";
import { getCookie, setCookie } from "cookies-next";
import { OptionsType } from "cookies-next/lib/types";
import { Session } from "next-auth";
import { useSession } from "next-auth/react";
import { useTranslations } from "next-intl";
import { redirect, useRouter } from "next/navigation";
import { usePathname } from "next/navigation";
import React, { Dispatch, FC, ReactNode, createContext, useCallback, useContext, useEffect, useReducer, useState } from "react";

import { InquiryDataType } from "@/components/StartChat/StartChat";
import { LoadingContext } from "@/context/LoaderContext";
import { languageChangeTracking, settingsChangeTracking } from "@/dataLayer/dataLayer";
import useClientInfo from "@/hooks/useClientInfo";
import { UserSettings } from "@/types";

import localesConfig from "../config/locales.config.json";

const recentSearchStorageKey = "recentSearch";
const storageCurrency = "currency";
const storageMeasurement = "measurement";
const storageLanguage = "language";
const storageCountry = "country";
const storageClientInfo = "clientInfo";

interface Props {
  children: ReactNode;
  settings: UserSettings;
}
interface UserProps {
  language: string;
  currency: "AED" | "USD" | "CNY" | "RUB" | "EUR" | "GBP" | "INR";
  measurement: "imperial" | "metric";
  changeLanguage: (userLanguage: string) => void;
  changeCurrencyAndMeasurement: (currency: string, measurement: string) => void;
  isLoggedIn: boolean;
  userSession: Session | undefined;
  updateLoginType: (type: string) => void;
  checkLoginError: (error: boolean) => void;
  loginType: string;
  loginError: boolean;
  loginSuccess: boolean;
  recentSearch: string[];
  setRecentSearch: Dispatch<string>;
  trendingSearch: string[];
  isResendOtpDisabled: boolean;
  updateIsResendOtpDisabled: (value: boolean) => void;
  fewResultsMaximum: number;
  isDialogueOpen: boolean;
  setIsDialogueOpen: (value: boolean) => void;

  isVideoModalOpenIframe: boolean;
  setIsVideoModalOpenIframe: (value: boolean) => void;
  checkoutLeadData?: InquiryDataType;
  setCheckoutLeadData: (data: InquiryDataType) => void;
  isCheckoutIntentModal: boolean;
  setIsCheckoutIntentModal: (value: boolean) => void;
  isCheckoutBackClicked?: boolean;
  setIsCheckoutBackClicked?: (value: boolean) => void;
  isInventoryAvailable: { slug: string; available: boolean };
  setIsInventoryAvailable?: (value: { slug: string; available: boolean }) => void;
  // country: string | any;
  // phonePrefix: string;
  // updatePhonePrefix: (prefix: string) => void;
}

export const UserContext = createContext<UserProps>({
  language: "",
  currency: DEFAULT_CURRENCY,
  measurement: DEFAULT_MEASUREMENT,
  changeLanguage: () => {},
  changeCurrencyAndMeasurement: () => {},
  isLoggedIn: false,
  userSession: undefined,
  updateLoginType: () => {},
  checkLoginError: () => {},
  loginType: "",
  loginError: false,
  loginSuccess: false,
  recentSearch: [],
  setRecentSearch: () => {},
  trendingSearch: [],
  isResendOtpDisabled: false,
  updateIsResendOtpDisabled: () => {},
  fewResultsMaximum: 0,
  isDialogueOpen: false,
  setIsDialogueOpen: () => {},
  isVideoModalOpenIframe: false,
  setIsVideoModalOpenIframe: () => {},
  setCheckoutLeadData: () => {},
  setIsCheckoutIntentModal: () => {},
  isCheckoutIntentModal: false,
  isCheckoutBackClicked: false,
  setIsCheckoutBackClicked: () => {},
  isInventoryAvailable: { slug: "", available: false },
  setIsInventoryAvailable: () => {},
  // country: "",
  // phonePrefix: "",
  // updatePhonePrefix: () => {},
});

const recentSearchReducer = (currentState: string[], newParam: string): string[] => {
  const categories = newParam.split(",");
  const recentSearch = [...new Set([...currentState, ...categories])].filter((item) => item !== "");
  setCookie(recentSearchStorageKey, recentSearch.join(","), cookieOptions);
  return recentSearch;
};

const initializeRecentSearch = () => {
  const existingValue = getCookie(recentSearchStorageKey) ?? "";
  const categories = existingValue.split(",");
  const recentSearch = [...new Set([...categories])].filter((item) => item !== "");
  return recentSearch;
};

const CookieExpirationDate = new Date();
CookieExpirationDate.setFullYear(CookieExpirationDate.getFullYear() + 10);
const cookieOptions: OptionsType = { expires: CookieExpirationDate, sameSite: "lax" };
const UserProvider: FC<Props> = ({ children, settings }) => {
  const { loading } = useContext(LoadingContext);
  const pathname = usePathname();
  const router = useRouter();
  const { data: session, status } = useSession();

  const userStorageLanguage = getCookie(storageLanguage)?.toLowerCase();
  const [language, setLanguage] = useState(userStorageLanguage || settings?.language);
  const [currency, setCurrency] = useState(settings.currency);
  const [measurement, setMeasurement] = useState(settings.measurement);
  const [country, setCountry] = useState(settings.country);
  // const [phonePrefix, setPhonePrefix] = useState("");
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [loginError, setLoginError] = useState(false);
  const [checkoutLeadData, setCheckoutLeadData] = useState<InquiryDataType>();
  const [isCheckoutIntentModal, setIsCheckoutIntentModal] = useState<boolean>(false);
  const [isCheckoutBackClicked, setIsCheckoutBackClicked] = useState<boolean>(false);
  const [loginSuccess, setLoginSuccess] = useState(false);
  const [isInventoryAvailable, setIsInventoryAvailable] = useState({ slug: "", available: false });
  const [userSession, setUserSession] = useState<Session>();
  const [loginType, setLoginType] = useState("");
  const client = useClientInfo({});
  const [isResendOtpDisabled, setIsResendOtpDisabled] = useState(true);
  const [isDialogueOpen, setIsDialogueOpen] = useState<boolean>(false);
  const [isVideoModalOpenIframe, setIsVideoModalOpenIframe] = useState<boolean>(false);

  /**
   * Search settings
   */
  const [recentSearch, setRecentSearch] = useReducer(recentSearchReducer, settings?.recentSearch, initializeRecentSearch);
  const [trendingSearch] = useState<string[]>(settings?.trendingSearch ?? []);
  const [fewResultsMaximum] = useState<number>(settings?.fewResultsMaximum ?? 5);
  const t = useTranslations();

  useEffect(() => {
    if (language) {
      setCookie(storageLanguage, language, cookieOptions);
    }
  }, [language]);

  useEffect(() => {
    if (currency) {
      setCookie(storageCurrency, currency, cookieOptions);
    }
  }, [currency]);
  useEffect(() => {
    if (country) {
      setCookie(storageCountry, country, cookieOptions);
    }
  }, [country]);
  useEffect(() => {
    if (measurement) {
      setCookie(storageMeasurement, measurement, cookieOptions);
    }
  }, [measurement]);

  const updateIsResendOtpDisabled = useCallback((value: boolean) => {
    setIsResendOtpDisabled(value);
  }, []);

  useEffect(() => {
    if (session) {
      setIsLoggedIn(true);
      setUserSession(userSession);
      if (pathname.includes("login")) {
        router.push("/my-profile");
      }
    }
    if (status !== "loading" && session === null && pathname.includes("my-profile")) {
      router.push("/login");
    }
  }, [pathname, router, session, status, setUserSession, userSession]);

  const changeLanguage = useCallback(
    (userLanguage: string) => {
      if (userLanguage && country && pathname) {
        const localePattern = new RegExp(regexForLangAndCountryCode);
        const shortLocaleRegexp = new RegExp(regexForLang);

        const newURLPathGroups = pathname.match(localePattern);
        const shortMatchWitoutCountry = pathname.match(shortLocaleRegexp);

        let newURL = "";
        // let targetLocale = `${userLanguage.toLowerCase()}-${country.toLowerCase()}`; // If want to keep the both locale-country logic then un-comment this line and below line comment
        let targetLocale = `${userLanguage.toLowerCase()}`;

        // Check if the locale exists in config, fallback to default if not
        if (!localesConfig.includes(targetLocale) && !localesConfig.includes(userLanguage)) {
          targetLocale = DEFAULT_LOCALE;
        } else if (!localesConfig.includes(targetLocale)) {
          targetLocale = userLanguage;
        }

        if (newURLPathGroups) {
          newURL = `/${targetLocale}/${newURLPathGroups[2] ?? ""}`;
        } else if (shortMatchWitoutCountry) {
          newURL = `/${targetLocale}/${shortMatchWitoutCountry[2] ?? ""}`;
        } else {
          newURL = `/${targetLocale}`;
        }

        if (newURL !== pathname) {
          loading(true);

          let language = targetLocale.includes("-") ? targetLocale.split("-")[0] : targetLocale;
          let countryCode = targetLocale.includes("-") ? targetLocale.split("-")[1].toUpperCase() : country;

          setCookie(storageLanguage, language, cookieOptions);
          setCookie(storageCountry, countryCode, cookieOptions);
          setLanguage(language);
          languageChangeTracking(language);
          router.replace(newURL);
          console.log("newURL", newURL, targetLocale, country);
          router.refresh();
        }
      }
    },
    [country, pathname, router],
  );

  // Detect route change completion and remove loading
  // Using this when language is changed then removed the Loading
  useEffect(() => {
    loading(false);
  }, [pathname]);

  // const updatePhonePrefix = useCallback((prefix: string) => {
  //   setPhonePrefix(prefix);
  // }, []);

  const checkLoginError = useCallback((isError: boolean) => {
    setLoginError(isError);
    setLoginSuccess(!isError);
    setTimeout(() => {
      setLoginError(false);
      setLoginSuccess(false);
    }, 5000);
  }, []);

  const changeCurrencyAndMeasurement = useCallback(
    (newCurrency: string, newMeasurement: string) => {
      // Update State
      setCurrency(newCurrency as any);
      setMeasurement(newMeasurement as any);

      // Save to Cookies so the useEffect above finds it on next load
      setCookie("usc", newCurrency, cookieOptions);
      setCookie("usu", newMeasurement, cookieOptions);
    },
    [setCurrency, setMeasurement],
  );

  const updateLoginType = useCallback((type: string) => {
    setCookie("loginType", type);
  }, []);

  useEffect(() => {
    if (client && !!typeof window) {
      const cookie = getCookie(storageClientInfo);
      const cookieValue = !!cookie ? JSON.parse(cookie as string) : {};
      const info = client.getResult();
      const newCookieValue = {
        ...cookieValue,
        ...{ browser: info.browser.name, os: info.os.name, resolution: `${window.screen.availWidth}x${window.screen.availHeight}` },
      };
      setCookie("clientInfo", JSON.stringify(newCookieValue), cookieOptions);
      const aid = new URLSearchParams(location.search).get("aid");
      const maxDay = Number(t("aid.expires.day")) || 20;
      if (aid) {
        setCookie("aid", aid, { maxAge: maxDay * 24 * 60 * 60 });
        sendGTMEvent({ event: "agent_id_set", value: aid });
      }
    }
  }, [client]);

  // Map country codes to their respective currencies
  const countryLanguageMap: any = {
    AE: { locale: "en-ae", currency: "AED" },
    US: { locale: "en-us", currency: "USD" },
    CN: { locale: "en-cn", currency: "CNY" },
    RU: { locale: "en-ru", currency: "RUB" },
    GB: { locale: "en-gb", currency: "GBP" },
    IN: { locale: "en-in", currency: "INR" },
    CA: { locale: "en-ca", currency: "USD" },
    AU: { locale: "en-au", currency: "USD" },
    SG: { locale: "en-sg", currency: "USD" },
    TR: { locale: "en-tr", currency: "USD" },
    IQ: { locale: "en-iq", currency: "USD" },
    IT: { locale: "en-it", currency: "EUR" },
    DE: { locale: "en-de", currency: "EUR" },
    FR: { locale: "en-fr", currency: "EUR" },
  };
  const cookieUserCountry = getCookie(storageUserCountry)?.toUpperCase() || DEFAULT_COUNTRY;
  /*
    Start => Update User Currency
  */
  useEffect(() => {
    // 1. Get the country from cookies and ensure it's a string (fallback to DEFAULT_COUNTRY)
    const cookieUserCountry = (getCookie(storageUserCountry)?.toUpperCase() || DEFAULT_COUNTRY).toString();

    // 2. Safely look up the regional currency
    const localeCurrency = countryLanguageMap[cookieUserCountry] || {
      locale: DEFAULT_LOCALE,
      currency: DEFAULT_CURRENCY,
    };

    // 3. Check if user has a manually saved currency in cookies
    const savedCurrency = getCookie("usc");

    if (savedCurrency) {
      // If a manual selection exists, use it
      setCurrency(savedCurrency as any);
    } else {
      // Otherwise, use the region-based currency and save it
      const finalCurrency = localeCurrency.currency || DEFAULT_CURRENCY;
      setCurrency(finalCurrency as any);
      setCookie(storageCurrency, finalCurrency, cookieOptions);
    }
  }, [country]); // Re-runs if the country state changes
  /*
    End => Update User Currency
  */

  return (
    <UserContext.Provider
      value={{
        language,
        currency,
        measurement,
        changeLanguage,
        changeCurrencyAndMeasurement,
        isLoggedIn,
        userSession,
        updateLoginType,
        loginType,
        checkLoginError,
        loginError,
        loginSuccess,
        recentSearch,
        setRecentSearch,
        trendingSearch,
        isResendOtpDisabled,
        updateIsResendOtpDisabled,
        fewResultsMaximum,
        isDialogueOpen,
        setIsDialogueOpen,
        isVideoModalOpenIframe,
        setIsVideoModalOpenIframe,
        checkoutLeadData,
        setCheckoutLeadData,
        isCheckoutIntentModal,
        setIsCheckoutIntentModal,
        isCheckoutBackClicked,
        setIsCheckoutBackClicked,
        isInventoryAvailable,
        setIsInventoryAvailable,
        // country,
        // phonePrefix,
        // updatePhonePrefix,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};
export default UserProvider;
